import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Pagination,
  CircularProgress,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupIcon from "@mui/icons-material/Group";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";

import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import SearchQueriesPage from "./SearchQueriesPage";
import DeleteUserPopup from "../../../components/DeleteUserPopup";
import { getUsersWithSubscription, searchUsersWithSubscription, deleteUser, getUploadsUrl } from "../../../services/adminService";

export default function RegisteredUsersPage() {
  // Initialize users as empty array to prevent undefined errors
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchMode, setSearchMode] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // API pagination state
  const [apiPagination, setApiPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    usersPerPage: 50,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const navigate = useNavigate();

  // Fetch users (supports optional server pagination)
  const fetchUsers = async (pageNumber = 1, perPage = apiPagination.usersPerPage || 50) => {
    try {
      if (!loading) setPaginationLoading(true);
      const response = await getUsersWithSubscription(pageNumber, perPage);
      // Normalize response to array + optional pagination
      let nextUsers = [];
      if (Array.isArray(response)) {
        nextUsers = response;
      } else if (response && Array.isArray(response.users)) {
        nextUsers = response.users;
      } else if (response && Array.isArray(response.data)) {
        nextUsers = response.data;
      }
      setUsers(nextUsers);
      // Try to pick pagination info if provided by API
      if (response && response.pagination) {
        setApiPagination(prev => ({
          ...prev,
          currentPage: response.pagination.currentPage || pageNumber,
          totalPages: response.pagination.totalPages || 1,
          totalUsers: response.pagination.totalUsers || nextUsers.length,
          usersPerPage: perPage || prev.usersPerPage || 50,
          hasNextPage: !!response.pagination.hasNextPage,
          hasPreviousPage: !!response.pagination.hasPreviousPage,
        }));
        setPage(response.pagination.currentPage || pageNumber);
      } else {
        // Fallback when backend doesn't send pagination meta
        setApiPagination(prev => ({
          ...prev,
          currentPage: pageNumber,
          totalPages: 1,
          totalUsers: Array.isArray(nextUsers) ? nextUsers.length : 0,
          usersPerPage: perPage || prev.usersPerPage || 50,
          hasNextPage: false,
          hasPreviousPage: false,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
      setApiPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0,
      }));
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1, apiPagination.usersPerPage || 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteUser = async () => {
    try {
      await deleteUser(userToDelete);
      setUsers((prev) => prev.filter((user) => user._id !== userToDelete));
      setShowDeletePopup(false);
      Swal.fire("Deleted!", "User deleted successfully.", "success");
    } catch (err) {
      console.error("Error deleting user:", err);
      Swal.fire("Error", "Failed to delete user.", "error");
    }
  };

  const filteredUsers = (() => {
    const input = searchInput.toLowerCase();
    
    // Ensure users is always an array
    if (!Array.isArray(users)) {
      return [];
    }
    const usersArray = users;

    if (!searchInput || !searchMode) return usersArray;

    const startsWithMatches = usersArray.filter((user) => {
      const value =
        searchMode === "name"
          ? `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase()
          : (user.email || "").toLowerCase();
      return value.startsWith(input);
    });

    if (startsWithMatches.length > 0) return startsWithMatches;

    return usersArray.filter((user) => {
      const value =
        searchMode === "name"
          ? `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase()
          : (user.email || "").toLowerCase();
      return value.includes(input);
    });
  })();

  // Ensure filteredUsers is always an array
  const safeFilteredUsers = Array.isArray(filteredUsers) ? filteredUsers : [];

  // The display list is driven by server pagination/search, or filtered locally if needed
  const displayUsers = isSearchActive || !searchMode
    ? (Array.isArray(users) ? users : [])
    : safeFilteredUsers;

  const getProfilePictureUrl = (profilePicture) =>
    getUploadsUrl(profilePicture);

  const handleBack = () => {
    setSelectedUserId(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    
    if (isSearchActive && searchInput.trim() && searchMode) {
      // If search is active, use search API for pagination
      handleSearchPagination(newPage);
    } else {
      // Otherwise use regular pagination
      fetchUsers(newPage, apiPagination.usersPerPage || 50);
    }
  };

  const handleSearchPagination = async (pageNumber) => {
    try {
      setPaginationLoading(true);
      
      const response = await searchUsersWithSubscription(
        searchInput.trim(),
        searchMode,
        pageNumber,
        apiPagination.usersPerPage || 50
      );
      
      if (response && response.users && Array.isArray(response.users)) {
        setUsers(response.users);
        
        if (response.pagination) {
          setApiPagination(prev => ({
            ...prev,
            currentPage: response.pagination.currentPage || pageNumber,
            totalPages: response.pagination.totalPages || 1,
            totalUsers: response.pagination.totalUsers || 0,
            usersPerPage: prev.usersPerPage || 50,
            hasNextPage: response.pagination.hasNextPage || false,
            hasPreviousPage: response.pagination.hasPreviousPage || false
          }));
          setPage(response.pagination.currentPage || pageNumber);
        } else {
          setApiPagination(prev => ({
            ...prev,
            currentPage: pageNumber
          }));
        }
      } else {
        setUsers([]);
        setApiPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalUsers: 0,
          currentPage: 1
        }));
      }
    } catch (err) {
      console.error("Search pagination failed:", err);
      setUsers([]);
      setApiPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalUsers: 0,
        currentPage: 1
      }));
    } finally {
      setPaginationLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setApiPagination(prev => ({
      ...prev,
      usersPerPage: newPageSize
    }));
    setPage(1); // Reset to first page when changing page size
    
    if (isSearchActive && searchInput.trim() && searchMode) {
      // If search is active, use search API with new page size
      handleSearchPagination(1);
    } else {
      // Otherwise use regular pagination
      fetchUsers(1, newPageSize);
    }
  };

  const handleSearch = async () => {
    if (!searchInput.trim() || !searchMode) {
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearchActive(true);
      setPage(1); // Reset to page 1 when searching
      
      
      const response = await searchUsersWithSubscription(
        searchInput.trim(),
        searchMode,
        1, // Start from page 1
        apiPagination.usersPerPage || 50
      );
      
      
      if (response && response.users && Array.isArray(response.users)) {
        setUsers(response.users);
        
        // Update API pagination state with safe defaults
        if (response.pagination) {
          setApiPagination(prev => ({
            ...prev,
            currentPage: response.pagination.currentPage || 1,
            totalPages: response.pagination.totalPages || 1,
            totalUsers: response.pagination.totalUsers || 0,
            usersPerPage: apiPagination.usersPerPage || 50,
            hasNextPage: response.pagination.hasNextPage || false,
            hasPreviousPage: response.pagination.hasPreviousPage || false
          }));
          setPage(response.pagination.currentPage || 1);
        } else {
          setApiPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalUsers: 0
          }));
        }
      } else {
        console.warn("Invalid search response format:", response);
        setUsers([]);
        setApiPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalUsers: 0,
          currentPage: 1
        }));
      }
    } catch (err) {
      console.error("Search failed:", err);
      setUsers([]);
      setApiPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalUsers: 0,
        currentPage: 1
      }));
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = async () => {
    setSearchInput("");
    setSearchMode("");
    setIsSearchActive(false);
    setPage(1);
    
    // Fetch all users again
    await fetchUsers(1, apiPagination.usersPerPage || 50);
  };

  if (selectedUserId) {
    return <SearchQueriesPage userId={selectedUserId} onBack={handleBack} />;
  }

  return (
    <AdminDashboardLayout>
      <Box
        sx={{
          px: 4,
          py: 3,
          bgcolor: "#f5f5f5",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        {/* Search Bar */}
        {showSearchBar && (
          <Paper
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 2,
              boxShadow: 2,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mr: 2, fontSize: "14px" }}
              >
                Basic search
              </Typography>
              
              {isSearchActive && (
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 0.5, 
                  px: 1, 
                  py: 0.5, 
                  bgcolor: "rgba(255, 107, 107, 0.1)", 
                  borderRadius: 1,
                  border: "1px solid rgba(255, 107, 107, 0.3)"
                }}>
                  <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                    Search Active
                  </Typography>
                </Box>
              )}
            </Box>

                          <ToggleButtonGroup
                value={searchMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setSearchMode(newMode);
                    setSearchInput("");
                    setIsSearchActive(false);
                    setPage(1);
                    // Clear search and fetch all users when changing search mode
                    if (isSearchActive) {
                      fetchUsers(1, apiPagination.usersPerPage || 50);
                    }
                  }
                }}
                sx={{ mr: 2 }}
              >
              <ToggleButton value="name" sx={{ fontSize: "12px", px: 2 }}>
                <GroupIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                Name
              </ToggleButton>
              <ToggleButton value="email" sx={{ fontSize: "12px", px: 2 }}>
                <GroupIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                Email
              </ToggleButton>
            </ToggleButtonGroup>

            {searchMode && (
              <>
                <TextField
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder={searchMode ? `Search by ${searchMode}...` : "Select search mode first"}
                  size="small"
                  sx={{
                    width: 200,
                    height: 36,
                    borderRadius: "8px",
                    mr: 1,
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      fontSize: "13px",
                    },
                  }}
                />
                
                <IconButton
                  onClick={handleSearch}
                  disabled={searchLoading || !searchInput.trim() || !searchMode}
                  sx={{
                    bgcolor: "#1BE885",
                    color: "#fff",
                    "&:hover": { bgcolor: "#16c973" },
                    height: "36px",
                    width: "36px"
                  }}
                  title="Search API for users"
                >
                  {searchLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon fontSize="small" />
                  )}
                </IconButton>

                {/* Clear Search Button */}
                {isSearchActive && (
                  <IconButton
                    onClick={clearSearch}
                    disabled={searchLoading}
                    sx={{
                      bgcolor: "#ff6b6b",
                      color: "#fff",
                      "&:hover": { bgcolor: "#ff5252" },
                      height: "36px",
                      width: "36px"
                    }}
                    title="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                
              </>
            )}
          </Paper>
        )}

        {/* User Table */}
        <Paper sx={{ width: "100%", overflowX: "auto" }}>
          <Box
            sx={{
              bgcolor: "#000",
              p: 1.5,
              px: 3,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}
            >
              All Users
            </Typography>
          </Box>

          {/* Search and Page Summary */}
          {!loading && (
            <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: isSearchActive ? "rgba(255, 107, 107, 0.1)" : "rgba(27, 232, 133, 0.05)", borderRadius: 1, border: isSearchActive ? "1px solid rgba(255, 107, 107, 0.3)" : "none" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {isSearchActive && searchInput && searchMode && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                    Search Results:
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                      {apiPagination.totalUsers} users found for "{searchInput}" in {searchMode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      (Showing page {page} of {apiPagination.totalPages})
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isSearchActive ? 
                    `Search: ${apiPagination.totalUsers} total results` : 
                    `Page ${page} of ${apiPagination.totalPages} (${apiPagination.totalUsers} total users)`
                  }
                </Typography>
                
               
              </Box>
            </Box>
          )}

          {loading ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : paginationLoading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={24} sx={{ color: "#1BE885" }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading page {page}...
              </Typography>
            </Box>
          ) : displayUsers.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                {displayUsers.length === 0 && users.length > 0 
                  ? "No users match your search criteria" 
                  : "No users found"}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        "#",
                        "Profile",
                        "Name",
                        "Email",
                        "Available Credits",
                        "Plan Expiry",
                        "Added On",
                        "Actions",
                      ].map((header, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            background: "#f1f1f1",
                            fontWeight: "medium",
                            fontSize: "12px",
                            color: "#222",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {displayUsers.map((user, index) => {
                      const fullName =
                        user.firstName || user.lastName
                          ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                          : "-";
                      const createdAt = user.createdAt
                        ? new Date(user.createdAt).toLocaleString()
                        : "-";
                      const profilePicUrl = getProfilePictureUrl(user.profilePicture);

                      return (
                        <TableRow key={user._id || index}>
                          <TableCell>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {profilePicUrl ? (
                              <Avatar src={profilePicUrl} />
                            ) : (
                              <Avatar sx={{ bgcolor: "#ccc", color: "#000" }}>
                                {user.firstName?.[0]?.toUpperCase() || "U"}
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>{fullName}</TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {user.email || "-"}
                          </TableCell>
                       {/* <TableCell>{user.totalCredits ?? 0}</TableCell> */}
                        <TableCell>{user.remainingCredits ?? 0}</TableCell>
                        <TableCell>
                          {user.planExpiry 
                            ? new Date(user.planExpiry).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit'
                              })
                            : "-"
                          }
                        </TableCell>
                          <TableCell>{createdAt}</TableCell>
                          <TableCell>
                            {/* <IconButton
                              color="primary"
                              onClick={() => setSelectedUserId(user._id)}
                              title="View User Profile"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton> */}

                            <IconButton
                              color="info"
                              onClick={() =>
                                navigate(`/admin/userprofiledetails/${user._id}`)
                              }
                              title="View User Profile"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              color="error"
                              onClick={() => {
                                setUserToDelete(user._id);
                                setShowDeletePopup(true);
                              }}
                              title="Delete User"
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" justifyContent="space-between" alignItems="center" py={2}>
                {/* Page Size Selector */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Show:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={apiPagination.usersPerPage}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      disabled={paginationLoading}
                      sx={{ height: "32px", fontSize: "12px" }}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    per page
                  </Typography>
                </Box>

                {/* Pagination with Loading */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {paginationLoading && (
                    <CircularProgress size={16} sx={{ color: "#1BE885" }} />
                  )}
                  <Pagination
                    count={Math.max(1, apiPagination.totalPages || 1)}
                    page={Math.max(1, page || 1)}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                    disabled={paginationLoading}
                  />
                </Box>
                
                {/* Page Info */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {page} of {apiPagination.totalPages} 
                    ({apiPagination.totalUsers} total users)
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Paper>

        {showDeletePopup && (
          <DeleteUserPopup
            onCancel={() => {
              setShowDeletePopup(false);
              Swal.fire("Cancelled", "User is safe!", "info");
            }}
            onConfirm={handleDeleteUser}
          />
        )}
      </Box>
    </AdminDashboardLayout>
  );
}